import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Gauge, MessageSquare, RefreshCw, Sparkles, X, Zap,
  TrendingUp, AlertTriangle, Target, ChevronRight, Loader2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const PINK = "var(--n-pink)";
const PINK_SOFT = "var(--n-pink-soft)";

const BAND_COLORS = {
  excellent: "#10B981",
  good:      "#14B8A6",
  average:   "#F59E0B",
  weak:      "#F97316",
  poor:      "#EF4444",
};

function bandColor(band, score) {
  if (band && BAND_COLORS[band]) return BAND_COLORS[band];
  if (score == null) return "var(--n-border)";
  if (score >= 85) return BAND_COLORS.excellent;
  if (score >= 70) return BAND_COLORS.good;
  if (score >= 55) return BAND_COLORS.average;
  if (score >= 40) return BAND_COLORS.weak;
  return BAND_COLORS.poor;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function Conversations() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sort, setSort] = useState("recent"); // "recent" | "score"
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      setError("");
      const [s, list] = await Promise.all([
        api.get("/conversations/intel/summary"),
        api.get("/conversations/intel/list", { params: { sort, limit: 500 } }),
      ]);
      setSummary(s.data);
      setRows(list.data || []);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [sort]);

  useEffect(() => { load(); }, [load]);

  const runBackfill = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/conversations/intel/backfill", { only_unanalyzed: true, limit: 25 });
      await load();
      setSummary((prev) => prev); // keep
      alert(`Backfill complete\nProcessed: ${data.processed}\nSuccess: ${data.success}\nFailed: ${data.failed}\nRemaining unanalyzed: ${data.remaining}`);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const analyzedRows = useMemo(() => rows.filter((r) => r.analyzed), [rows]);
  const unanalyzedCount = useMemo(() => rows.filter((r) => !r.analyzed).length, [rows]);

  return (
    <div className="max-w-[1200px] mx-auto p-6 md:p-10" data-testid="ci-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center"
               style={{ background: PINK_SOFT, border: `1px solid ${PINK}55` }}>
            <MessageSquare size={20} style={{ color: PINK }} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-black" style={{ color: PINK }}>
              Phase 1 · Live
            </div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tight" style={{ color: "var(--n-text)" }}>
              Conversation Intelligence
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => { setLoading(true); load(); }}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border text-sm font-semibold"
            style={{ borderColor: "var(--n-border)", color: "var(--n-text)", background: "var(--n-card)" }}
            data-testid="ci-refresh-btn"
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            type="button"
            onClick={runBackfill}
            disabled={busy || unanalyzedCount === 0}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg text-sm font-black disabled:opacity-60"
            style={{ background: PINK, color: "white", boxShadow: `0 10px 30px -14px ${PINK}` }}
            data-testid="ci-backfill-btn"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {busy ? "Grading…" : `Grade ${Math.min(unanalyzedCount, 25)} unanalyzed`}
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="ci-summary">
        <SummaryTile label="Conversations" value={summary?.conversation_total ?? "—"} />
        <SummaryTile label="Graded" value={summary?.analyzed_success ?? "—"} accent={BAND_COLORS.good} />
        <SummaryTile label="Unanalyzed" value={summary?.unanalyzed ?? "—"} accent={PINK} />
        <SummaryTile label="Failed" value={summary?.analyzed_failed ?? "—"} accent={BAND_COLORS.poor} />
        <SummaryTile
          label="Avg score"
          value={summary?.avg_score != null ? `${summary.avg_score}` : "—"}
          suffix={summary?.avg_score != null ? "/100" : ""}
          accent={bandColor(null, summary?.avg_score)}
        />
      </div>

      {/* Band distribution */}
      {summary?.bands && summary.analyzed_success > 0 && (
        <div className="rounded-xl p-4 mb-6 border" style={{ borderColor: "var(--n-border)", background: "var(--n-card)" }}>
          <div className="flex items-center gap-2 mb-3">
            <Gauge size={14} style={{ color: "var(--n-text-sec)" }} />
            <div className="text-[11px] uppercase tracking-[0.2em] font-black" style={{ color: "var(--n-text-sec)" }}>
              Score distribution
            </div>
          </div>
          <div className="flex gap-1 h-3 rounded-full overflow-hidden" style={{ background: "var(--n-border)" }}>
            {Object.entries(summary.bands).map(([b, c]) => (
              <div key={b} title={`${b}: ${c}`}
                   style={{ background: BAND_COLORS[b], width: `${(c / (summary.analyzed_success || 1)) * 100}%` }} />
            ))}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[11px]" style={{ color: "var(--n-text-sec)" }}>
            {Object.entries(summary.bands).map(([b, c]) => (
              <div key={b} className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: BAND_COLORS[b] }} />
                <span className="capitalize">{b}</span> <span className="font-mono">{c}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sort toggle */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[11px] uppercase tracking-[0.2em] font-black" style={{ color: "var(--n-text-sec)" }}>Sort</span>
        <SortPill label="Recent" active={sort === "recent"} onClick={() => setSort("recent")} testid="ci-sort-recent" />
        <SortPill label="Score ↓" active={sort === "score"} onClick={() => setSort("score")} testid="ci-sort-score" />
      </div>

      {error && (
        <div className="rounded-xl p-3 mb-4 border text-sm" style={{ borderColor: `${BAND_COLORS.poor}66`, background: `${BAND_COLORS.poor}18`, color: BAND_COLORS.poor }} data-testid="ci-error">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--n-border)", background: "var(--n-card)" }}>
        <div className="grid grid-cols-[70px,1fr,120px,120px,90px] md:grid-cols-[80px,1fr,140px,160px,110px] items-center px-4 py-3 text-[10px] font-black uppercase tracking-[0.2em]"
             style={{ color: "var(--n-text-sec)", background: "var(--n-card-alt, rgba(255,255,255,0.03))", borderBottom: "1px solid var(--n-border)" }}>
          <div>Score</div>
          <div>Visitor / last message</div>
          <div className="hidden md:block">Band</div>
          <div>Updated</div>
          <div className="text-right">Detail</div>
        </div>
        {loading ? (
          <div className="p-6 text-center text-sm" style={{ color: "var(--n-text-sec)" }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-sm" style={{ color: "var(--n-text-sec)" }}>
            No conversations yet. They&apos;ll appear here as visitors chat with Kit.
          </div>
        ) : rows.map((r) => (
          <button key={r.conversation_id}
                  type="button"
                  onClick={() => setOpenId(r.conversation_id)}
                  className="w-full text-left grid grid-cols-[70px,1fr,120px,120px,90px] md:grid-cols-[80px,1fr,140px,160px,110px] items-center px-4 py-3 hover:bg-white/[0.03] transition-colors border-t"
                  style={{ borderColor: "var(--n-border)" }}
                  data-testid={`ci-row-${r.conversation_id}`}>
            <ScoreBadge score={r.overall_score} band={r.band} analyzed={r.analyzed} />
            <div className="min-w-0 pr-3">
              <div className="font-bold text-sm truncate" style={{ color: "var(--n-text)" }}>
                {r.visitor_name}
                {r.lead_id && (
                  <span className="ml-2 inline-block text-[9px] font-black uppercase tracking-[0.2em] px-1.5 py-0.5 rounded"
                        style={{ background: `${BAND_COLORS.good}22`, color: BAND_COLORS.good }}>
                    Lead
                  </span>
                )}
              </div>
              <div className="text-xs mt-0.5 truncate" style={{ color: "var(--n-text-sec)" }}>
                {r.last_message_preview || "—"}
              </div>
            </div>
            <div className="hidden md:block text-xs capitalize font-semibold"
                 style={{ color: r.band ? BAND_COLORS[r.band] : "var(--n-text-sec)" }}>
              {r.band || (r.analysis_status === "failed" ? "failed" : "not graded")}
            </div>
            <div className="text-xs font-mono" style={{ color: "var(--n-text-sec)" }}>
              {fmtDate(r.analyzed_at || r.last_message_at)}
            </div>
            <div className="text-right">
              <ChevronRight size={16} style={{ color: "var(--n-text-sec)" }} />
            </div>
          </button>
        ))}
      </div>

      {openId && (
        <ScorecardDrawer
          conversationId={openId}
          onClose={() => setOpenId(null)}
          onGraded={load}
        />
      )}
    </div>
  );
}

function SummaryTile({ label, value, suffix = "", accent }) {
  return (
    <div className="rounded-xl p-3 border" style={{ borderColor: "var(--n-border)", background: "var(--n-card)" }}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-black" style={{ color: "var(--n-text-sec)" }}>{label}</div>
      <div className="mt-1 text-2xl font-black" style={{ color: accent || "var(--n-text)" }}>
        {value}<span className="text-sm opacity-60 font-bold">{suffix}</span>
      </div>
    </div>
  );
}

function SortPill({ label, active, onClick, testid }) {
  return (
    <button type="button" onClick={onClick}
            className="text-xs font-semibold px-3 h-8 rounded-full border"
            style={{
              background: active ? PINK : "transparent",
              color: active ? "white" : "var(--n-text-sec)",
              borderColor: active ? PINK : "var(--n-border)",
            }}
            data-testid={testid}>
      {label}
    </button>
  );
}

function ScoreBadge({ score, band, analyzed }) {
  if (!analyzed || score == null) {
    return (
      <div className="w-12 h-12 rounded-full flex items-center justify-center text-[10px] font-black uppercase tracking-wider"
           style={{ background: "var(--n-card-alt, rgba(255,255,255,0.03))", color: "var(--n-text-sec)", border: "1px dashed var(--n-border)" }}>
        —
      </div>
    );
  }
  const color = bandColor(band, score);
  return (
    <div className="w-12 h-12 rounded-full flex flex-col items-center justify-center font-black"
         style={{ background: `${color}18`, color, border: `1.5px solid ${color}` }}>
      <div className="text-base leading-none">{score}</div>
      <div className="text-[8px] opacity-70 leading-none mt-0.5">/100</div>
    </div>
  );
}

// ---------- Scorecard drawer ----------
function ScorecardDrawer({ conversationId, onClose, onGraded }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/conversations/${conversationId}/analysis`);
      setData(data);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const regrade = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/conversations/${conversationId}/analyze`);
      await load();
      onGraded && onGraded();
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const a = data?.analysis;
  const messages = data?.messages || [];

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="ci-drawer">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative ml-auto w-full md:w-[min(1100px,95vw)] h-full overflow-y-auto"
           style={{ background: "var(--n-bg, #0B1524)", borderLeft: "1px solid var(--n-border)" }}>
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b"
             style={{ borderColor: "var(--n-border)", background: "var(--n-bg, #0B1524)" }}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                 style={{ background: PINK_SOFT, border: `1px solid ${PINK}55` }}>
              <MessageSquare size={16} style={{ color: PINK }} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] font-black" style={{ color: "var(--n-text-sec)" }}>
                Scorecard
              </div>
              <div className="font-black truncate" style={{ color: "var(--n-text)" }}>
                {(data?.conversation?.visitor?.name) || "Chat visitor"} · {messages.length} messages
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={regrade} disabled={busy}
                    className="inline-flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-semibold disabled:opacity-60"
                    style={{ background: PINK, color: "white" }}
                    data-testid="ci-regrade-btn">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
              {a ? "Regrade" : "Grade now"}
            </button>
            <button type="button" onClick={onClose}
                    className="w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{ background: "var(--n-card)", color: "var(--n-text)", border: "1px solid var(--n-border)" }}
                    data-testid="ci-drawer-close">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="p-5 md:p-6 grid grid-cols-1 lg:grid-cols-[1fr,1fr] gap-6">
          {/* Transcript */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-black mb-3" style={{ color: "var(--n-text-sec)" }}>
              Transcript
            </div>
            <div className="space-y-2 rounded-xl border p-3 max-h-[70vh] overflow-y-auto"
                 style={{ borderColor: "var(--n-border)", background: "var(--n-card)" }}
                 data-testid="ci-transcript">
              {loading ? (
                <div className="text-sm p-3" style={{ color: "var(--n-text-sec)" }}>Loading…</div>
              ) : messages.length === 0 ? (
                <div className="text-sm p-3" style={{ color: "var(--n-text-sec)" }}>No messages yet.</div>
              ) : messages.map((m) => (
                <MessageBubble key={m.id} msg={m} />
              ))}
            </div>
          </div>

          {/* Analysis */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-black mb-3" style={{ color: "var(--n-text-sec)" }}>
              Analysis
            </div>
            {loading ? (
              <div className="rounded-xl border p-4 text-sm" style={{ borderColor: "var(--n-border)", background: "var(--n-card)", color: "var(--n-text-sec)" }}>
                Loading…
              </div>
            ) : error ? (
              <div className="rounded-xl p-3 border text-sm"
                   style={{ borderColor: `${BAND_COLORS.poor}66`, background: `${BAND_COLORS.poor}18`, color: BAND_COLORS.poor }}>
                {error}
              </div>
            ) : !a ? (
              <div className="rounded-xl border p-4 text-sm" style={{ borderColor: "var(--n-border)", background: "var(--n-card)", color: "var(--n-text-sec)" }}>
                Not graded yet. Hit <strong>Grade now</strong> to run the evaluator.
              </div>
            ) : a.status !== "success" ? (
              <div className="rounded-xl border p-4 text-sm"
                   style={{ borderColor: `${BAND_COLORS.poor}66`, background: `${BAND_COLORS.poor}18`, color: BAND_COLORS.poor }}>
                Evaluator returned status <strong>{a.status}</strong>. {a.error}
              </div>
            ) : (
              <AnalysisPanel a={a} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ msg }) {
  const isBot = msg.sender_role === "ai";
  const isAdmin = msg.sender_role === "admin";
  const isVisitor = msg.sender_role === "visitor" || msg.from_visitor;
  const label = isBot ? "BOT" : isAdmin ? "ADMIN" : isVisitor ? "VISITOR" : (msg.sender_role || "").toUpperCase();
  const color = isBot ? BAND_COLORS.good : isAdmin ? "#8B5CF6" : "#F59E0B";
  return (
    <div className="rounded-lg p-3 border" style={{ borderColor: "var(--n-border)", background: "rgba(255,255,255,0.02)" }}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[9px] font-black uppercase tracking-[0.2em] px-1.5 py-0.5 rounded" style={{ background: `${color}22`, color }}>
          {label}
        </span>
        <span className="text-[10px] font-mono" style={{ color: "var(--n-text-sec)" }}>{fmtDate(msg.created_at)}</span>
      </div>
      <div className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: "var(--n-text)" }}>{msg.body}</div>
    </div>
  );
}

function AnalysisPanel({ a }) {
  const color = bandColor(a.band, a.overall_score);
  return (
    <div className="space-y-4">
      {/* Overall */}
      <div className="rounded-xl border p-4 flex items-center gap-4"
           style={{ borderColor: `${color}55`, background: `${color}0e` }}
           data-testid="ci-overall">
        <div className="w-20 h-20 rounded-full flex flex-col items-center justify-center font-black shrink-0"
             style={{ background: `${color}22`, color, border: `2px solid ${color}` }}>
          <div className="text-2xl leading-none">{a.overall_score}</div>
          <div className="text-[9px] opacity-70 leading-none mt-1">/ 100</div>
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] font-black" style={{ color }}>
            {a.band} · v{a.prompt_version} · {a.provider}/{a.model?.split("-").slice(0, 3).join("-")}
          </div>
          <div className="mt-1 text-sm leading-relaxed" style={{ color: "var(--n-text)" }}>
            {a.summary || "—"}
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--n-border)", background: "var(--n-card)" }}>
        {(a.categories || []).map((c) => (
          <CategoryRow key={c.key} c={c} />
        ))}
      </div>

      {/* Insight lists */}
      {a.strengths?.length > 0 && <InsightList icon={<TrendingUp size={13} />} title="Strengths" items={a.strengths} color={BAND_COLORS.good} />}
      {a.weaknesses?.length > 0 && <InsightList icon={<AlertTriangle size={13} />} title="Weaknesses" items={a.weaknesses} color={BAND_COLORS.weak} />}
      {a.missed_opportunities?.length > 0 && <InsightList icon={<Target size={13} />} title="Missed opportunities" items={a.missed_opportunities} color={PINK} />}
      {a.buying_signals_detected?.length > 0 && <InsightList icon={<Sparkles size={13} />} title="Buying signals detected" items={a.buying_signals_detected} color={BAND_COLORS.good} inline />}
      {a.next_best_action && (
        <div className="rounded-xl border p-3" style={{ borderColor: `${PINK}44`, background: `${PINK}0e` }} data-testid="ci-nba">
          <div className="text-[10px] uppercase tracking-[0.22em] font-black" style={{ color: PINK }}>Next best action</div>
          <div className="mt-1 text-sm" style={{ color: "var(--n-text)" }}>{a.next_best_action}</div>
        </div>
      )}
    </div>
  );
}

function CategoryRow({ c }) {
  const pct = c.max ? Math.round((c.score / c.max) * 100) : 0;
  const color = pct >= 85 ? BAND_COLORS.excellent : pct >= 70 ? BAND_COLORS.good : pct >= 55 ? BAND_COLORS.average : pct >= 40 ? BAND_COLORS.weak : BAND_COLORS.poor;
  return (
    <div className="p-3 border-b last:border-b-0" style={{ borderColor: "var(--n-border)" }} data-testid={`ci-cat-${c.key}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-sm font-bold" style={{ color: "var(--n-text)" }}>{c.label}</div>
        <div className="text-xs font-mono" style={{ color }}>{c.score} / {c.max}</div>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--n-border)" }}>
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
      {c.notes && <div className="text-xs mt-2" style={{ color: "var(--n-text-sec)" }}>{c.notes}</div>}
      {c.evidence && (
        <blockquote className="text-xs mt-1.5 pl-3 border-l-2 italic"
                    style={{ borderColor: `${color}77`, color: "var(--n-text-sec)" }}>
          &ldquo;{c.evidence}&rdquo;
        </blockquote>
      )}
    </div>
  );
}

function InsightList({ icon, title, items, color, inline = false }) {
  return (
    <div className="rounded-xl border p-3" style={{ borderColor: `${color}44`, background: `${color}0e` }}>
      <div className="flex items-center gap-1.5 mb-2">
        <span style={{ color }}>{icon}</span>
        <div className="text-[10px] uppercase tracking-[0.22em] font-black" style={{ color }}>{title}</div>
      </div>
      {inline ? (
        <div className="flex flex-wrap gap-1.5">
          {items.map((it, i) => (
            <span key={i} className="text-[11px] font-semibold px-2 py-1 rounded-full"
                  style={{ background: `${color}22`, color, border: `1px solid ${color}55` }}>{it}</span>
          ))}
        </div>
      ) : (
        <ul className="text-sm space-y-1 list-disc pl-4" style={{ color: "var(--n-text)" }}>
          {items.map((it, i) => <li key={i}>{it}</li>)}
        </ul>
      )}
    </div>
  );
}
